import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { todayIso as todayIsoUtil } from '../utils/dateUtils';

const MODULES = ['ALL', 'PMS', 'KOT', 'BAR', 'BANQUET', 'STORES', 'STORES_CONSUMPTION'];

const STATUS_STYLE = {
  SUCCESS:  { bg: '#dcfce7', color: '#166534' },
  FAILED:   { bg: '#fee2e2', color: '#991b1b' },
  PENDING:  { bg: '#f3f4f6', color: '#6b7280' },
  PUSHING:  { bg: '#dbeafe', color: '#1e40af' },
};

const MODULE_STYLE = {
  PMS:     { bg: '#dbeafe', color: '#1e40af' },
  KOT:     { bg: '#d1fae5', color: '#065f46' },
  BAR:     { bg: '#fde68a', color: '#92400e' },
  BANQUET: { bg: '#e9d5ff', color: '#6b21a8' },
  STORES: { bg: '#ccfbf1', color: '#0f766e' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: 12,
      padding: '2px 10px', fontSize: 11, fontWeight: 700,
      display: 'inline-block', whiteSpace: 'nowrap'
    }}>
      {status || '-'}
    </span>
  );
}

function ModuleBadge({ module }) {
  const s = MODULE_STYLE[module] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: 4,
      padding: '1px 8px', fontSize: 10, fontWeight: 700,
      display: 'inline-block', whiteSpace: 'nowrap'
    }}>
      {module || '-'}
    </span>
  );
}

function fmtAmount(v) {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function todayStr() { return todayIsoUtil(); }

export default function TallyLivePush({ token, selectedProperty }) {
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [module, setModule] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('');

  const [summary, setSummary] = useState({ total: 0, pushed: 0, failed: 0, pending: 0 });
  const [logRows, setLogRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Push state
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState({ current: 0, total: 0, statusText: '' });

  // XML modal
  const [xmlModal, setXmlModal] = useState(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  // ── Load summary ──
  const loadSummary = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      const res = await fetch(`/api/v1/tally-push/push/summary?${params}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load summary');
      const data = await res.json();
      setSummary({
        total: Number(data.total || 0),
        pushed: Number(data.pushed || 0),
        failed: Number(data.failed || 0),
        pending: Number(data.pending || 0)
      });
    } catch (err) {
      // silent — log table will show error if needed
    }
  }, [selectedProperty, fromDate, toDate, headers]);

  // ── Load log ──
  const loadLog = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      if (module !== 'ALL') params.append('module', module);
      if (statusFilter) params.append('status', statusFilter);
      const res = await fetch(`/api/v1/tally-push/push/log?${params}`, { headers: headers() });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to load push log');
      }
      const data = await res.json();
      setLogRows(Array.isArray(data) ? data : data.rows || data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load push log');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, fromDate, toDate, module, statusFilter, headers]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLog(); }, [loadLog]);

  // ── Push to Tally ──
  const handlePush = async () => {
    if (pushing) return;
    setPushing(true);
    setPushProgress({ current: 0, total: 0, statusText: 'Initiating push to Tally...' });
    setError('');
    try {
      const body = { from_date: fromDate, to_date: toDate, module: module === 'ALL' ? undefined : module };
      const res = await fetch('/api/v1/tally-push/push', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Push failed');
      }
      const result = await res.json();
      const total = Number(result.total || 0);
      const pushed = Number(result.pushed || 0);
      const failed = Number(result.failed || 0);
      const skipped = Number(result.skipped || 0);
      setPushProgress({
        current: total,
        total,
        statusText: `Completed: ${pushed} pushed, ${failed} failed, ${skipped} skipped`
      });
      // Reload data
      await Promise.all([loadSummary(), loadLog()]);
    } catch (err) {
      setError(err.message || 'Push failed');
      setPushProgress(prev => ({ ...prev, statusText: `Error: ${err.message}` }));
    } finally {
      setPushing(false);
    }
  };

  // ── Retry failed ──
  const handleRetryAll = async () => {
    if (pushing) return;
    setPushing(true);
    setPushProgress({ current: 0, total: 0, statusText: 'Retrying failed vouchers...' });
    setError('');
    try {
      const failedRows = logRows.filter(r => r.status === 'FAILED');
      const total = failedRows.length;
      let done = 0;
      for (const row of failedRows) {
        setPushProgress({ current: done, total, statusText: `Retrying ${done + 1} of ${total}...` });
        try {
          const res = await fetch(`/api/v1/tally-push/push/${row.log_id}/retry`, {
            method: 'POST',
            headers: headers()
          });
          if (res.ok) {
            const updated = await res.json();
            setLogRows(prev => prev.map(r => r.log_id === row.log_id ? { ...r, ...updated, status: updated.status || 'SUCCESS' } : r));
          }
        } catch (_) {
          // continue with next
        }
        done++;
      }
      setPushProgress({ current: total, total, statusText: `Retry complete. Processed ${total} vouchers.` });
      await Promise.all([loadSummary(), loadLog()]);
    } catch (err) {
      setError(err.message || 'Retry failed');
    } finally {
      setPushing(false);
    }
  };

  // ── Retry single row ──
  const handleRetrySingle = async (logId) => {
    try {
      setLogRows(prev => prev.map(r => r.log_id === logId ? { ...r, status: 'PUSHING' } : r));
      const res = await fetch(`/api/v1/tally-push/push/${logId}/retry`, {
        method: 'POST',
        headers: headers()
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Retry failed');
      }
      const updated = await res.json();
      setLogRows(prev => prev.map(r => r.log_id === logId ? { ...r, ...updated, status: updated.status || 'SUCCESS' } : r));
      await loadSummary();
    } catch (err) {
      setLogRows(prev => prev.map(r => r.log_id === logId ? { ...r, status: 'FAILED', error_message: err.message } : r));
    }
  };

  const hasFailedRows = useMemo(() => logRows.some(r => r.status === 'FAILED'), [logRows]);

  const progressPct = pushProgress.total > 0
    ? Math.round((pushProgress.current / pushProgress.total) * 100)
    : 0;

  // ── Styles ──
  const cardStyle = {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '14px 20px', flex: '1 1 140px', minWidth: 140, textAlign: 'center'
  };
  const filterCardStyle = {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '16px 20px', marginBottom: 16
  };
  const btnPrimary = {
    padding: '8px 24px', background: '#1a365d', color: '#fff', border: 'none',
    borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: 'pointer',
    opacity: pushing ? 0.7 : 1
  };
  const btnWarning = {
    padding: '8px 20px', background: '#d97706', color: '#fff', border: 'none',
    borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
    opacity: pushing ? 0.7 : 1
  };
  const moduleBtn = (active) => ({
    padding: '5px 14px', border: '1px solid #d1d5db', borderRadius: 4,
    fontSize: 12, fontWeight: active ? 700 : 400,
    background: active ? '#1a365d' : '#fff',
    color: active ? '#fff' : '#374151', cursor: 'pointer'
  });
  const thStyle = {
    padding: '7px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb',
    fontSize: 11, fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap',
    background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1
  };
  const tdStyle = {
    padding: '6px 10px', borderBottom: '1px solid #f3f4f6',
    fontSize: 12, whiteSpace: 'nowrap'
  };
  const tdMono = {
    ...tdStyle, fontFamily: "'Courier New', Courier, monospace"
  };
  const tdRight = {
    ...tdMono, textAlign: 'right'
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Please select a property first.</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#1a365d' }}>
        Tally Live Push
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
        Push accounting vouchers directly to Tally from PMS, KOT, BAR, and Banquet modules.
      </p>

      {/* ── Filter Bar ── */}
      <div style={filterCardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2, fontWeight: 600 }}>From</label>
            <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2, fontWeight: 600 }}>To</label>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2, fontWeight: 600 }}>Module</label>
            <div style={{ display: 'flex', gap: 2 }}>
              {MODULES.map(m => (
                <button key={m} onClick={() => setModule(m)} style={moduleBtn(module === m)}>
                  {m === 'ALL' ? 'All' : m}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginLeft: 'auto' }}>
            <button onClick={handlePush} disabled={pushing} style={btnPrimary}>
              {pushing ? 'Pushing...' : 'Push to Tally'}
            </button>
            {hasFailedRows && (
              <button onClick={handleRetryAll} disabled={pushing} style={btnWarning}>
                Retry Failed
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Summary Cards ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Total Vouchers</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1a365d', marginTop: 2 }}>{summary.total}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Pushed</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#166534', marginTop: 2 }}>{summary.pushed}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Failed</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#991b1b', marginTop: 2 }}>{summary.failed}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Pending</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#6b7280', marginTop: 2 }}>{summary.pending}</div>
        </div>
      </div>

      {/* ── Progress Section ── */}
      {pushing && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
          padding: '14px 20px', marginBottom: 16
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
              {pushProgress.total > 0
                ? `Pushing ${pushProgress.current} of ${pushProgress.total} vouchers...`
                : 'Preparing vouchers...'}
            </span>
            <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 700 }}>
              {pushProgress.total > 0 ? `${progressPct}%` : ''}
            </span>
          </div>
          <div style={{
            background: '#dbeafe', borderRadius: 6, height: 8, overflow: 'hidden'
          }}>
            <div style={{
              background: '#2563eb', height: '100%', borderRadius: 6,
              width: pushProgress.total > 0 ? `${progressPct}%` : '30%',
              transition: 'width 0.3s ease',
              animation: pushProgress.total === 0 ? 'none' : undefined
            }} />
          </div>
          {pushProgress.statusText && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
              {pushProgress.statusText}
            </div>
          )}
        </div>
      )}

      {/* Non-pushing completion message */}
      {!pushing && pushProgress.statusText && pushProgress.total > 0 && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
          padding: '10px 20px', marginBottom: 16, fontSize: 13, color: '#166534', fontWeight: 600
        }}>
          {pushProgress.statusText}
        </div>
      )}

      {/* ── Push Log Table ── */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
        overflow: 'hidden'
      }}>
        {/* Table toolbar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#fafafa'
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
            Push Log ({logRows.length} entries)
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Status:</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12 }}>
              <option value="">All</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
              <option value="PUSHING">Pushing</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center', fontSize: 13 }}>Loading...</div>
        ) : logRows.length === 0 ? (
          <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center', fontSize: 13 }}>
            No push log entries found for the selected filters.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 480 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Module</th>
                  <th style={{ ...thStyle, fontFamily: "'Courier New', Courier, monospace" }}>Voucher No</th>
                  <th style={thStyle}>Type</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Debit</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Credit</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                  <th style={thStyle}>Error</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logRows.map((row, idx) => (
                  <tr key={row.log_id || idx} style={{ background: idx % 2 ? '#f9fafb' : '#fff' }}>
                    <td style={tdStyle}>{row.voucher_date || row.push_date || '-'}</td>
                    <td style={tdStyle}><ModuleBadge module={row.module} /></td>
                    <td style={tdMono}>{row.voucher_no || '-'}</td>
                    <td style={tdStyle}>{row.voucher_type || row.type || '-'}</td>
                    <td style={tdRight}>{fmtAmount(row.debit_amount)}</td>
                    <td style={tdRight}>{fmtAmount(row.credit_amount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={row.status} /></td>
                    <td style={{
                      ...tdStyle, color: row.error_message ? '#991b1b' : '#9ca3af',
                      maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 11
                    }}>
                      {row.error_message || '-'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        {row.status === 'FAILED' && (
                          <button
                            onClick={() => handleRetrySingle(row.log_id)}
                            style={{
                              padding: '3px 10px', background: '#fef3c7', color: '#92400e',
                              border: '1px solid #fcd34d', borderRadius: 4, fontSize: 11,
                              fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Retry
                          </button>
                        )}
                        {row.xml_payload && (
                          <button
                            onClick={() => setXmlModal({ voucher_no: row.voucher_no, xml: row.xml_payload })}
                            style={{
                              padding: '3px 10px', background: '#f3f4f6', color: '#374151',
                              border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11,
                              fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            View XML
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── XML Preview Modal ── */}
      {xmlModal && (
        <div
          onClick={() => setXmlModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 10, width: '90%', maxWidth: 720,
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px', borderBottom: '1px solid #e5e7eb'
            }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1a365d' }}>
                XML Payload {xmlModal.voucher_no ? `- ${xmlModal.voucher_no}` : ''}
              </span>
              <button
                onClick={() => setXmlModal(null)}
                style={{
                  background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
                  color: '#6b7280', fontWeight: 700, lineHeight: 1, padding: '0 4px'
                }}
              >
                x
              </button>
            </div>
            {/* Modal body */}
            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
              <pre style={{
                background: '#1e293b', color: '#e2e8f0', borderRadius: 6,
                padding: 16, fontSize: 12, fontFamily: "'Courier New', Courier, monospace",
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
                maxHeight: '55vh', overflowY: 'auto', lineHeight: 1.5
              }}>
                {xmlModal.xml || '(No XML payload available)'}
              </pre>
            </div>
            {/* Modal footer */}
            <div style={{
              padding: '10px 20px', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'flex-end', gap: 8
            }}>
              <button
                onClick={() => {
                  if (xmlModal.xml) {
                    navigator.clipboard.writeText(xmlModal.xml).catch(() => {});
                  }
                }}
                style={{
                  padding: '6px 16px', background: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Copy
              </button>
              <button
                onClick={() => setXmlModal(null)}
                style={{
                  padding: '6px 16px', background: '#1a365d', color: '#fff',
                  border: 'none', borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
